// repositories/_errorFromJsonApi.js
//
// Converts an Axios error from a JSON:API endpoint into a rich Error object
// that callers can inspect without reaching into nested response structures.
//
// Priority for the human-readable message:
//   1. errors[0].detail  — localized backend sentence (new API contract)
//   2. data.message      — legacy plain-message field (kept for rollout compat)
//   3. fallbackMessage   — i18n key or static string supplied by the caller
//
// Additional properties attached to the returned Error:
//   .code     — errors[0].code (e.g. 'auth.no_active_membership') or null
//   .meta     — errors[0].meta (e.g. `{ left: 2 }` on 'tadbeesa.not_enough_pieces') or null
//   .errors   — the full errors array from the JSON:API body
//   .status   — HTTP status code or null
//
// Usage:
//   throw errorFromJsonApi(error, 'orders.errors.createFailed');
//
// Callers can then do:
//   if (e.code === 'auth.no_active_membership') { /* show lifecycle card */ }

/**
 * @param {unknown} error - The Axios error (or any caught value).
 * @param {string} [fallbackMessage='An error occurred'] - Used when both
 *   errors[0].detail and data.message are absent.
 * @returns {Error & { code: string|null, meta: object|null, errors: Array, status: number|null }}
 */
export function errorFromJsonApi(error, fallbackMessage = 'An error occurred') {
  const errors = error?.response?.data?.errors ?? [];
  const detail = errors[0]?.detail ?? null;
  const legacyMessage = error?.response?.data?.message ?? null;

  const message = detail ?? legacyMessage ?? fallbackMessage;

  const e = new Error(message);
  e.code = errors[0]?.code ?? null;
  e.meta = errors[0]?.meta ?? null;
  e.errors = errors;
  e.status = error?.response?.status ?? null;

  return e;
}
